/**
 * Infrastructure Sync & Import Utilities
 */

import {
  InfrastructureSyncConfig,
  InfrastructureDiff,
  InfrastructureProvider,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement, ElementType, Layer } from './types';
import { uuidv7 } from 'uuidv7';

export function createSyncConfig(provider: InfrastructureProvider): InfrastructureSyncConfig {
  const defaultMappings = getDefaultResourceMappings(provider);

  return {
    provider,
    resources: defaultMappings,
    syncFrequency: 'manual',
  };
}

function getDefaultResourceMappings(
  provider: InfrastructureProvider
): Array<{ type: string; mapping: ElementType }> {
  switch (provider) {
    case 'aws':
      return [
        { type: 'AWS::EC2::Instance', mapping: 'node' },
        { type: 'AWS::RDS::DBInstance', mapping: 'node' },
        { type: 'AWS::Lambda::Function', mapping: 'application-component' },
        { type: 'AWS::ECS::Service', mapping: 'application-component' },
        { type: 'AWS::EKS::Cluster', mapping: 'node' },
        { type: 'AWS::S3::Bucket', mapping: 'artifact' },
        { type: 'AWS::DynamoDB::Table', mapping: 'artifact' },
        { type: 'AWS::VPC::VPC', mapping: 'communication-network' },
        { type: 'AWS::ELB::LoadBalancer', mapping: 'node' },
        { type: 'AWS::CloudFront::Distribution', mapping: 'node' },
      ];

    case 'azure':
      return [
        { type: 'Microsoft.Compute/virtualMachines', mapping: 'node' },
        { type: 'Microsoft.Sql/servers', mapping: 'node' },
        { type: 'Microsoft.Web/sites', mapping: 'application-component' },
        { type: 'Microsoft.ContainerService/managedClusters', mapping: 'node' },
        { type: 'Microsoft.Storage/storageAccounts', mapping: 'artifact' },
        { type: 'Microsoft.Network/virtualNetworks', mapping: 'communication-network' },
        { type: 'Microsoft.Network/loadBalancers', mapping: 'node' },
      ];

    case 'gcp':
      return [
        { type: 'compute.v1.instance', mapping: 'node' },
        { type: 'sqladmin.v1beta4.instance', mapping: 'node' },
        { type: 'run.googleapis.com/Service', mapping: 'application-component' },
        { type: 'container.v1.cluster', mapping: 'node' },
        { type: 'storage.v1.bucket', mapping: 'artifact' },
        { type: 'compute.v1.network', mapping: 'communication-network' },
      ];

    case 'kubernetes':
      return [
        { type: 'Deployment', mapping: 'application-component' },
        { type: 'Service', mapping: 'application-service' },
        { type: 'StatefulSet', mapping: 'application-component' },
        { type: 'Pod', mapping: 'application-component' },
        { type: 'Namespace', mapping: 'grouping' },
        { type: 'Ingress', mapping: 'technology-interface' },
        { type: 'ConfigMap', mapping: 'artifact' },
        { type: 'Secret', mapping: 'artifact' },
      ];

    case 'terraform':
      // Terraform would map based on resource types defined in HCL
      return [
        { type: 'aws_instance', mapping: 'node' },
        { type: 'aws_lambda_function', mapping: 'application-component' },
        { type: 'aws_rds_instance', mapping: 'node' },
        { type: 'aws_s3_bucket', mapping: 'artifact' },
      ];
  }
}

export function parseInfrastructureData(
  provider: InfrastructureProvider,
  data: any,
  config: InfrastructureSyncConfig
): ArchiMateElement[] {
  switch (provider) {
    case 'aws':
      return parseAWSData(data, config);
    case 'azure':
      return parseAzureData(data, config);
    case 'gcp':
      return parseGCPData(data, config);
    case 'kubernetes':
      return parseKubernetesData(data, config);
    case 'terraform':
      return parseTerraformData(data, config);
  }
}

function parseAWSData(data: any, config: InfrastructureSyncConfig): ArchiMateElement[] {
  const elements: ArchiMateElement[] = [];

  // Example: Parse CloudFormation stack or AWS Config data
  if (data.StackResources) {
    data.StackResources.forEach((resource: any) => {
      const mapping = config.resources.find((r) => r.type === resource.ResourceType);
      if (mapping) {
        elements.push({
          id: uuidv7(),
          type: mapping.mapping,
          layer: getLayerForType(mapping.mapping),
          name: resource.LogicalResourceId || resource.PhysicalResourceId,
          documentation: `AWS Resource: ${resource.ResourceType}`,
          properties: {
            provider: 'aws',
            resourceType: resource.ResourceType,
            resourceId: resource.PhysicalResourceId,
            status: resource.ResourceStatus,
          },
          x: 0,
          y: 0,
          width: 120,
          height: 60,
        });
      }
    });
  }

  return elements;
}

function parseAzureData(data: any, config: InfrastructureSyncConfig): ArchiMateElement[] {
  const elements: ArchiMateElement[] = [];

  // Parse Azure Resource Manager (ARM) template or resource list
  if (data.resources) {
    data.resources.forEach((resource: any) => {
      const mapping = config.resources.find((r) => r.type === resource.type);
      if (mapping) {
        elements.push({
          id: uuidv7(),
          type: mapping.mapping,
          layer: getLayerForType(mapping.mapping),
          name: resource.name,
          documentation: `Azure Resource: ${resource.type}`,
          properties: {
            provider: 'azure',
            resourceType: resource.type,
            resourceId: resource.id,
            location: resource.location,
          },
          x: 0,
          y: 0,
          width: 120,
          height: 60,
        });
      }
    });
  }

  return elements;
}

function parseGCPData(data: any, config: InfrastructureSyncConfig): ArchiMateElement[] {
  const elements: ArchiMateElement[] = [];

  // Parse GCP Deployment Manager or resource list
  if (data.resources) {
    data.resources.forEach((resource: any) => {
      const mapping = config.resources.find((r) => r.type === resource.type);
      if (mapping) {
        elements.push({
          id: uuidv7(),
          type: mapping.mapping,
          layer: getLayerForType(mapping.mapping),
          name: resource.name,
          documentation: `GCP Resource: ${resource.type}`,
          properties: {
            provider: 'gcp',
            resourceType: resource.type,
            resourceId: resource.name,
          },
          x: 0,
          y: 0,
          width: 120,
          height: 60,
        });
      }
    });
  }

  return elements;
}

function parseKubernetesData(data: any, config: InfrastructureSyncConfig): ArchiMateElement[] {
  const elements: ArchiMateElement[] = [];

  // Parse Kubernetes manifest or cluster state
  if (data.items) {
    data.items.forEach((item: any) => {
      const mapping = config.resources.find((r) => r.type === item.kind);
      if (mapping) {
        elements.push({
          id: uuidv7(),
          type: mapping.mapping,
          layer: getLayerForType(mapping.mapping),
          name: item.metadata?.name || 'unnamed',
          documentation: `Kubernetes ${item.kind}`,
          properties: {
            provider: 'kubernetes',
            kind: item.kind,
            namespace: item.metadata?.namespace,
            labels: JSON.stringify(item.metadata?.labels || {}),
          },
          x: 0,
          y: 0,
          width: 120,
          height: 60,
        });
      }
    });
  }

  return elements;
}

function parseTerraformData(data: any, config: InfrastructureSyncConfig): ArchiMateElement[] {
  const elements: ArchiMateElement[] = [];

  // Parse Terraform state file
  if (data.resources) {
    data.resources.forEach((resource: any) => {
      const resourceType = `${resource.type}`;
      const mapping = config.resources.find((r) => r.type === resourceType);

      if (mapping) {
        resource.instances?.forEach((instance: any) => {
          elements.push({
            id: uuidv7(),
            type: mapping.mapping,
            layer: getLayerForType(mapping.mapping),
            name: resource.name,
            documentation: `Terraform resource: ${resourceType}`,
            properties: {
              provider: 'terraform',
              resourceType: resourceType,
              module: resource.module,
              mode: resource.mode,
            },
            x: 0,
            y: 0,
            width: 120,
            height: 60,
          });
        });
      }
    });
  }

  return elements;
}

function getLayerForType(type: ElementType): Layer {
  if (type.includes('application')) return 'application';
  if (type.includes('business')) return 'business';
  if (type.includes('technology') || type.includes('node') || type.includes('artifact')) {
    return 'technology';
  }
  if (type.includes('physical')) return 'physical';
  return 'technology'; // default
}

export function compareWithInfrastructure(
  diagram: ExtendedArchiMateDiagram,
  liveElements: ArchiMateElement[]
): InfrastructureDiff {
  const diagramElements = new Map(
    diagram.elements
      .filter((e) => e.properties?.provider) // Only compare elements that came from infrastructure
      .map((e) => [e.properties!.resourceId, e])
  );

  const liveElementsMap = new Map(
    liveElements.map((e) => [e.properties!.resourceId, e])
  );

  const added: Array<{ name: string; type: string }> = [];
  const removed: Array<{ name: string; type: string; elementId: string }> = [];
  const modified: Array<{ name: string; type: string; elementId: string; changes: string[] }> = [];
  let unchanged = 0;

  // Find added elements (in live but not in diagram)
  liveElementsMap.forEach((liveEl, resourceId) => {
    if (!diagramElements.has(resourceId)) {
      added.push({ name: liveEl.name, type: liveEl.type });
    }
  });

  // Find removed elements (in diagram but not in live)
  diagramElements.forEach((diagramEl, resourceId) => {
    if (!liveElementsMap.has(resourceId)) {
      removed.push({
        name: diagramEl.name,
        type: diagramEl.type,
        elementId: diagramEl.id,
      });
    } else {
      // Check for modifications
      const liveEl = liveElementsMap.get(resourceId)!;
      const changes: string[] = [];

      if (diagramEl.name !== liveEl.name) {
        changes.push(`Name: ${diagramEl.name} → ${liveEl.name}`);
      }

      if (diagramEl.type !== liveEl.type) {
        changes.push(`Type: ${diagramEl.type} → ${liveEl.type}`);
      }

      if (changes.length > 0) {
        modified.push({
          name: diagramEl.name,
          type: diagramEl.type,
          elementId: diagramEl.id,
          changes,
        });
      } else {
        unchanged++;
      }
    }
  });

  return { added, removed, modified, unchanged };
}

export function generateDriftReport(diff: InfrastructureDiff): string {
  let report = `# Infrastructure Drift Report\n\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  report += `## Summary\n\n`;
  report += `- ➕ Added: ${diff.added.length}\n`;
  report += `- ➖ Removed: ${diff.removed.length}\n`;
  report += `- ✏️ Modified: ${diff.modified.length}\n`;
  report += `- ✅ Unchanged: ${diff.unchanged}\n\n`;

  if (diff.added.length > 0) {
    report += `## ➕ Added Resources\n\n`;
    diff.added.forEach((item) => {
      report += `- ${item.name} (${item.type})\n`;
    });
    report += `\n`;
  }

  if (diff.removed.length > 0) {
    report += `## ➖ Removed Resources\n\n`;
    diff.removed.forEach((item) => {
      report += `- ${item.name} (${item.type})\n`;
    });
    report += `\n`;
  }

  if (diff.modified.length > 0) {
    report += `## ✏️ Modified Resources\n\n`;
    diff.modified.forEach((item) => {
      report += `- **${item.name}** (${item.type})\n`;
      item.changes.forEach((change) => {
        report += `  - ${change}\n`;
      });
    });
    report += `\n`;
  }

  return report;
}

export function autoLayoutImportedElements(elements: ArchiMateElement[]): ArchiMateElement[] {
  // Simple grid layout for imported elements
  const cols = Math.ceil(Math.sqrt(elements.length));
  const spacing = 150;

  return elements.map((element, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);

    return {
      ...element,
      x: col * spacing + 50,
      y: row * spacing + 50,
    };
  });
}

export function exportInfrastructureReport(diagram: ExtendedArchiMateDiagram): string {
  let report = `# Infrastructure Report\n\n`;
  report += `**Diagram:** ${diagram.name}\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  // Group by provider
  const byProvider = new Map<string, ArchiMateElement[]>();

  diagram.elements.forEach((element) => {
    const provider = element.properties?.provider;
    if (provider) {
      if (!byProvider.has(provider)) {
        byProvider.set(provider, []);
      }
      byProvider.get(provider)!.push(element);
    }
  });

  report += `## Infrastructure Components\n\n`;
  report += `**Total elements:** ${diagram.elements.length}\n\n`;

  byProvider.forEach((elements, provider) => {
    report += `### ${provider.toUpperCase()}\n\n`;
    report += `**Count:** ${elements.length}\n\n`;

    elements.forEach((element) => {
      report += `- **${element.name}** (${element.type})\n`;
      if (element.documentation) {
        report += `  ${element.documentation}\n`;
      }
    });

    report += `\n`;
  });

  return report;
}

// Simplified import functions for backward compatibility
export function importFromTerraform(terraformState: string, diagram: ExtendedArchiMateDiagram): ExtendedArchiMateDiagram {
  const config = createSyncConfig('terraform');
  const parsedData = JSON.parse(terraformState);
  const elements = parseInfrastructureData('terraform', parsedData, config);

  return {
    ...diagram,
    elements: [...diagram.elements, ...elements],
  };
}

export function importFromAWS(awsConfig: string, diagram: ExtendedArchiMateDiagram): ExtendedArchiMateDiagram {
  const config = createSyncConfig('aws');
  const parsedData = JSON.parse(awsConfig);
  const elements = parseInfrastructureData('aws', parsedData, config);

  return {
    ...diagram,
    elements: [...diagram.elements, ...elements],
  };
}

export function importFromAzure(azureConfig: string, diagram: ExtendedArchiMateDiagram): ExtendedArchiMateDiagram {
  const config = createSyncConfig('azure');
  const parsedData = JSON.parse(azureConfig);
  const elements = parseInfrastructureData('azure', parsedData, config);

  return {
    ...diagram,
    elements: [...diagram.elements, ...elements],
  };
}

export function importFromGCP(gcpConfig: string, diagram: ExtendedArchiMateDiagram): ExtendedArchiMateDiagram {
  const config = createSyncConfig('gcp');
  const parsedData = JSON.parse(gcpConfig);
  const elements = parseInfrastructureData('gcp', parsedData, config);

  return {
    ...diagram,
    elements: [...diagram.elements, ...elements],
  };
}
